import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '../../ui/form';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { useToast } from '../../ui/use-toast';
import { supabase } from '../../../lib/supabase';
import { Image, X, User, Hash, Trophy, Loader2 } from 'lucide-react';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const contestantSchema = z.object({
  name: z.string().min(1, 'Le nom est requis'),
  description: z.string().min(1, 'La description est requise'),
  unique_code: z.string().min(1, 'Le code unique est requis'),
  contest_id: z.string().min(1, 'Le concours est requis'),
  number: z
    .union([
      z.string().regex(/^\d+$/, 'Numéro invalide').transform(Number),
      z.number(),
      z.null()
    ])
    .optional(),
  image: z
    .instanceof(FileList)
    .optional()
    .refine((files) => {
      if (files && files.length > 0) {
        return files[0].size <= MAX_FILE_SIZE;
      }
      return true;
    }, 'La taille maximale du fichier est de 5MB')
    .refine((files) => {
      if (files && files.length > 0) {
        return ACCEPTED_IMAGE_TYPES.includes(files[0].type);
      }
      return true;
    }, 'Format accepté : .jpg, .jpeg, .png, .webp'),
});

type ContestantFormValues = z.infer<typeof contestantSchema>;

interface Contest {
  id: string;
  name: string;
  status: string;
  end_date: string;
}

interface EditContestantDialogProps {
  contestant: {
    id: string;
    name: string;
    description: string;
    unique_code: string;
    contest_id: string;
    photo_url?: string;
    number?: number;
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function EditContestantDialog({
  contestant,
  open,
  onOpenChange,
  onSuccess,
}: EditContestantDialogProps) {
  const { toast } = useToast();
  const [contests, setContests] = useState<Contest[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(contestant.photo_url || null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const form = useForm<ContestantFormValues>({
    resolver: zodResolver(contestantSchema),
    defaultValues: {
      name: contestant.name,
      description: contestant.description,
      unique_code: contestant.unique_code,
      contest_id: contestant.contest_id,
      number: contestant.number,
    },
  });

  useEffect(() => {
    const fetchContests = async () => {
      const { data, error } = await supabase
        .from('contests')
        .select('id, name, status, end_date')
        .order('name');

      if (error) {
        toast({
          variant: 'destructive',
          title: 'Erreur',
          description: 'Impossible de charger les concours',
        });
      } else {
        // Exclure les concours terminés (status = 'completed' ou end_date < aujourd'hui)
        const now = new Date();
        const filtered = (data || []).filter((contest: any) => {
          if (contest.status === 'completed') return false;
          if (contest.end_date && new Date(contest.end_date) < now) return false;
          return true;
        });
        setContests(filtered);
      }
    };

    fetchContests();
  }, []);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview(contestant.photo_url || null);
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${contestant.id}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('candidates')
      .upload(filePath, file, {
        upsert: true,
      });

    if (uploadError) {
      throw uploadError;
    }

    const { data } = supabase.storage
      .from('candidates')
      .getPublicUrl(filePath);

    return data.publicUrl;
  };

  const handleDeleteImage = async () => {
    try {
      setIsDeleting(true);
      
      // Supprimer l'image de Supabase Storage
      if (contestant.photo_url) {
        const fileName = contestant.photo_url.split('/').pop();
        if (fileName) {
          await supabase.storage
            .from('candidates')
            .remove([fileName]);
        }
      }

      // Mettre à jour le candidat sans l'URL de l'image
      const { error } = await supabase
        .from('contestants')
        .update({ photo_url: null })
        .eq('id', contestant.id);

      if (error) throw error;

      setImagePreview(null);
      toast({
        title: 'Succès',
        description: "La photo a été supprimée",
      });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: error.message || "Impossible de supprimer la photo",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const onSubmit = async (data: ContestantFormValues) => {
    try {
      setIsUploading(true);
      let imageUrl = contestant.photo_url;

      // Upload new image if provided
      if (data.image && data.image.length > 0) {
        imageUrl = await uploadImage(data.image[0]);
      }

      const { error } = await supabase
        .from('contestants')
        .update({
          name: data.name,
          description: data.description,
          unique_code: data.unique_code,
          contest_id: data.contest_id,
          photo_url: imageUrl,
          number: data.number,
        })
        .eq('id', contestant.id);

      if (error) throw error;

      toast({
        title: "Candidat mis à jour",
        description: "Les modifications ont été enregistrées avec succès",
        variant: "default",
      });

      onSuccess?.();
      onOpenChange(false);
      // Recharge la page pour refléter les modifications comme lors de la création
      window.location.reload();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de mettre à jour le candidat",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-900 border border-white/10 text-white max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl font-bold bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200 text-transparent bg-clip-text">
            Modifier le candidat
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto space-y-6 pr-6 -mr-6">
              <FormField
                control={form.control}
                name="image"
                render={({ field: { onChange, value, ...field } }) => (
                  <FormItem>
                    <FormLabel className="text-gray-200">Photo</FormLabel>
                    <FormControl>
                      <div className="space-y-4">
                        {/* Zone de drag & drop */}
                        <div 
                          className="relative w-40 h-40 rounded-xl border-2 border-dashed border-yellow-400/30 bg-white/5 flex items-center justify-center cursor-pointer group hover:border-yellow-400 transition-all"
                          onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                          onDrop={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            const file = e.dataTransfer.files?.[0];
                            if (file && ACCEPTED_IMAGE_TYPES.includes(file.type) && file.size <= MAX_FILE_SIZE) {
                              const dt = new DataTransfer();
                              dt.items.add(file);
                              // Simule le changement sur l'input caché
                              const input = document.getElementById('image-upload') as HTMLInputElement;
                              if (input) input.files = dt.files;
                              onChange(dt.files);
                              handleImageChange({ target: { files: dt.files } } as any);
                            }
                          }}
                        >
                          {imagePreview ? (
                            <img
                              src={imagePreview}
                              alt="Aperçu"
                              className="w-full h-full object-cover rounded-xl"
                            />
                          ) : (
                            <div className="flex flex-col items-center justify-center text-gray-400">
                              <Image className="h-10 w-10 mb-2" />
                              <span>Glissez-déposez une image<br/>ou cliquez</span>
                            </div>
                          )}
                          {/* Bouton suppression */}
                          {imagePreview && (
                            <button
                              type="button"
                              onClick={handleDeleteImage}
                              disabled={isDeleting}
                              className="absolute top-2 right-2 p-2 bg-red-500/80 hover:bg-red-500 rounded-xl text-white transition-colors opacity-80 hover:opacity-100"
                            >
                              {isDeleting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <X className="h-4 w-4" />
                              )}
                            </button>
                          )}
                          {/* Input caché pour compatibilité */}
                          <Input
                            type="file"
                            accept={ACCEPTED_IMAGE_TYPES.join(',')}
                            onChange={e => {
                              onChange(e.target.files);
                              handleImageChange(e);
                            }}
                            {...field}
                            className="hidden"
                            id="image-upload"
                          />
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage className="text-red-400" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-200">Nom du candidat</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <User className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                        <Input
                          {...field}
                          className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                          placeholder="John Doe"
                        />
                      </div>
                    </FormControl>
                    <FormMessage className="text-red-400" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-200">Description</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50 min-h-[100px]"
                        placeholder="Décrivez le candidat..."
                      />
                    </FormControl>
                    <FormMessage className="text-red-400" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="unique_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-200">Code unique</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Hash className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                        <Input
                          {...field}
                          className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50 font-mono"
                          placeholder="CAND123"
                        />
                      </div>
                    </FormControl>
                    <FormDescription className="text-gray-400">
                      Code unique pour identifier le candidat
                    </FormDescription>
                    <FormMessage className="text-red-400" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Numéro du candidat</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        placeholder="Ex: 12 (laisser vide si non défini)"
                        value={field.value === undefined || field.value === null ? '' : field.value}
                        onChange={e => {
                          const val = e.target.value;
                          field.onChange(val === '' ? undefined : Number(val));
                        }}
                        {...field}
                        className="bg-yellow-50 text-yellow-900 border-yellow-300 focus:ring-yellow-400 focus:border-yellow-400 placeholder-yellow-400 font-bold tracking-widest shadow-inner"
                      />
                    </FormControl>
                    <FormDescription className="text-yellow-700">Numéro d'identification unique pour ce concours.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="contest_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-200">Concours</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Trophy className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                        <select
                          {...field}
                          className="w-full pl-10 pr-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-transparent appearance-none"
                        >
                          <option value="" className="bg-gray-900">Sélectionner un concours</option>
                          {contests.map((contest) => (
                            <option key={contest.id} value={contest.id} className="bg-gray-900">
                              {contest.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </FormControl>
                    <FormMessage className="text-red-400" />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter className="flex-shrink-0 pt-6 border-t border-white/10 mt-6">
              <Button
                type="submit"
                disabled={isUploading}
                className="bg-gradient-to-r from-yellow-600 to-yellow-500 hover:from-yellow-500 hover:to-yellow-400 text-white shadow-lg shadow-yellow-500/20 hover:shadow-yellow-500/40"
              >
                {isUploading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Mise à jour...
                  </div>
                ) : (
                  'Mettre à jour'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
